<script lang="ts">
import { computed, defineComponent, h, ref, shallowRef, toRefs, unref, watch, withDirectives } from 'vue';

import { Touch } from '@components/directives';
import { classNames } from '@components/utils';

import {
  makeAppearanceProps,
  makeDirectionProps,
  makeDisabledProps,
  makeGroupProps,
  makeIconProps,
  makeLocaleProps,
  makeTagProps,
  makeThemeProps,
  provideTheme,
  useAppearance,
  useDirection,
  useDisabled,
  useGroup,
  useLocale,
  useRender
} from '../../composable';
import { CdsButton } from '../CdsButton';

import { CdsContainerGroupSymbol, useProvideContainer } from './context';

import type { TCdsIconsName } from '@central-design-system/icons';
import type { ITouchHandlers } from '@components/directives/touch';
import type { PropType } from 'vue';

export const componentName = 'CdsContainer';
export default defineComponent({
  name: componentName,
  directives: { Touch },
  props: {
    /**
     * Показывать только один контейнер из группы
     */
    onlySingle: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Показывать стрелки для переключения между контейнерами в режиме onlySingle
     */
    showArrows: {
      type: [Boolean, String] as PropType<boolean | string>,
      validator: (value: any) => typeof value === 'boolean' || value === 'hover'
    },
    /**
     * Активирует переключение от последнего контейнера к первому и наоборот
     */
    continuous: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Активирует переключение контейнеров по свайпу
     */
    touch: {
      type: [Boolean, Object] as PropType<boolean | ITouchHandlers>,
      default: undefined
    },

    ...makeGroupProps(),
    ...makeAppearanceProps(),
    ...makeDirectionProps(),
    ...makeLocaleProps(),
    ...makeTagProps(),
    ...makeDisabledProps(),
    ...makeThemeProps(),
    ...makeIconProps({
      prependIcon: 'arrow-left' as TCdsIconsName,
      appendIcon: 'arrow-right' as TCdsIconsName
    })
  },
  emits: {
    'update:modelValue': (value: any) => true
  },
  setup(props, { slots, expose }) {
    const group = useGroup(props, CdsContainerGroupSymbol);
    const { themeClasses } = provideTheme(props);
    const { appearanceClasses } = useAppearance(props, componentName);
    const { disabledClasses } = useDisabled(props);
    const { directionClasses } = useDirection(props, componentName);
    const { container: containerLocale } = useLocale(props);
    const { onlySingle, multiple } = toRefs(props);

    const rootRef = ref();
    const isReversed = shallowRef(false);
    const transition = computed(() => {
      const axis = props.direction === 'vertical' ? 'y' : 'x';
      const direction = unref(isReversed) ? '-reverse' : '';

      return `cds-container-${axis}${direction}-transition`;
    });
    const transitionCount = shallowRef(0);
    const transitionHeight = ref<undefined | string>(undefined);

    const activeIndex = computed(() => unref(group.items).findIndex((item) => unref(group.selected).includes(item.id)));

    watch(activeIndex, (newValue, oldValue) => {
      const itemsLength = unref(group.items).length;
      const lastIndex = itemsLength - 1;

      if (itemsLength <= 2) {
        isReversed.value = newValue < oldValue;
      } else if (newValue === lastIndex && oldValue === 0) {
        isReversed.value = true;
      } else if (newValue === 0 && oldValue === lastIndex) {
        isReversed.value = false;
      } else {
        isReversed.value = newValue < oldValue;
      }
    });

    useProvideContainer({
      transition,
      rootRef,
      onlySingle,
      multiple,
      isReversed,
      transitionCount,
      transitionHeight
    });

    const hasSlideNavigation = computed(() => props.onlySingle && props.showArrows !== false);
    const canMoveBack = computed(() => props.continuous || unref(activeIndex) !== 0);
    const canMoveForward = computed(() => props.continuous || unref(activeIndex) !== unref(group.items).length - 1);

    function prev() {
      unref(canMoveBack) && group.prev();
    }

    function next() {
      unref(canMoveForward) && group.next();
    }

    const arrows = computed(() => {
      const arrows = [];

      const prevProps = {
        icon: props.prependIcon,
        class: 'cds-container__arrow-left',
        onClick: group.prev,
        ariaLabel: containerLocale.prev
      };

      arrows.push(
        unref(canMoveBack)
          ? slots.prev
            ? slots.prev({ props: prevProps })
            : h(CdsButton, {
                appearance: 'transparent',
                ...prevProps
              })
          : h('div')
      );

      const nextProps = {
        icon: props.appendIcon,
        class: 'cds-container__arrow-right',
        onClick: group.next,
        ariaLabel: containerLocale.next
      };

      arrows.push(
        canMoveForward.value
          ? slots.next
            ? slots.next({ props: nextProps })
            : h(CdsButton, { appearance: 'transparent', ...nextProps })
          : h('div')
      );

      return arrows;
    });

    const touchOptions = computed(() => {
      if (props.touch === false) return props.touch;

      const options: ITouchHandlers = {
        left: next,
        right: prev,
        start: ({ originalEvent }) => {
          originalEvent.stopPropagation();
        }
      };

      return {
        ...options,
        ...(props.touch === true ? {} : props.touch)
      };
    });

    useRender(() =>
      h(
        props.tag,
        {
          ref: rootRef,
          class: classNames(
            'cds-container',
            unref(appearanceClasses),
            unref(directionClasses),
            unref(disabledClasses),
            unref(themeClasses),
            {
              'cds-container--show-arrows-on-hover': props.showArrows === 'hover',
              'cds-container--only-single': props.onlySingle
            }
          )
        },
        withDirectives(
          h(
            'div',
            {
              class: 'cds-container__container'
            },
            [
              slots.default?.(group),
              unref(hasSlideNavigation) && h('div', { class: 'cds-container__arrows' }, unref(arrows))
            ]
          ),
          [[Touch, unref(touchOptions)]]
        )
      )
    );

    expose({
      group,
      next,
      prev
    });
  }
});
</script>

<style lang="scss">
@import './CdsContainer';
</style>
